import { forwardRef } from 'react';

import * as SwitchPrimitive from '@radix-ui/react-switch';

import {
  checkedOffLabelClass,
  checkedOnLabelClass,
  offLabelClass,
  onLabelClass,
  switchClass,
  thumbClass,
} from './ToggleSwitch.css';
import { useI18nTranslations } from '../../providers/i18n';
import { getClassNames } from '../../util/css';
import { Label } from '../Input/InputLabel';
import { Stack } from '../Stack/Stack';

type LabelProps =
  | {
      label: string;
      'aria-labelledby'?: never;
    }
  | {
      'aria-labelledby': string;
      label?: never;
    };

type ToggleSwitchProps = {
  name: string;
  value?: HTMLInputElement['value'];
  onCheckedChange?(checked: boolean): void;
  isChecked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
} & LabelProps;

export const ToggleSwitch = forwardRef<HTMLButtonElement, ToggleSwitchProps>(
  (
    { label, name, value, onCheckedChange, isChecked, defaultChecked, disabled, 'aria-labelledby': ariaLabelledby },
    ref,
  ) => {
    const { on, off } = useI18nTranslations();

    return (
      <div>
        <Stack gap="3">
          {label ? <Label htmlFor={name}>{label}</Label> : null}
          <SwitchPrimitive.Root
            className={switchClass}
            id={name}
            name={name}
            onCheckedChange={onCheckedChange}
            defaultChecked={defaultChecked}
            checked={isChecked}
            disabled={disabled}
            aria-labelledby={ariaLabelledby}
            ref={ref}
            value={value}>
            <span className={getClassNames(onLabelClass, checkedOnLabelClass)} aria-hidden="true">
              {on}
            </span>
            <span className={getClassNames(offLabelClass, checkedOffLabelClass)} aria-hidden="true">
              {off}
            </span>
            <SwitchPrimitive.Thumb className={thumbClass} />
          </SwitchPrimitive.Root>
        </Stack>
      </div>
    );
  },
);

ToggleSwitch.displayName = 'ToggleSwitch';
